import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';
import { useMutation, useReactiveVar } from '@apollo/client';
import { formatDate, getFirstDayOfTheWeek } from '@shared/services/dateService';
import ADD_ORG_UNIT_UNAVAILABLE_PERIOD from '../queries/UnavailablePeriods/AddOrgUnitUnavailablePeriodMutation';
import ADD_UNAVAILABLE_PERIOD from '../queries/UnavailablePeriods/AddUnavailablePeriodMutation';
import DELETE_ORG_UNIT_UNAVAILABLE_PERIOD from '../queries/UnavailablePeriods/deleteOrgUnitUnavailablePeriodMutation';
import DELETE_UNAVAILABLE_PERIOD from '../queries/UnavailablePeriods/deleteUnavailablePeriodMutation';
import GET_ORG_UNIT_UNAVAILABLE_PERIODS from '../queries/UnavailablePeriods/getOrgUnitUnavailablePeriodsQuery';
import GET_UNAVAILABLE_PERIODS from '../queries/UnavailablePeriods/getUnavailablePeriodsQuery';
import { userPrivileges } from '@apollo-internal/cache';
import PrivilegeNames from '../../../constants/privilegeNames';
import { addPeriodToCache, removePeriodFromCache } from '../services/updateUnavailablePeriodsCache';
import moment from 'moment';

const useAvailability = (selectedDate, selectedUser = null) => {
  const privileges = useReactiveVar(userPrivileges);
  const canAccessAtmList = privileges.includes(PrivilegeNames.Calendar_ViewEvents_AtmUsers);
  const canAccessUnitAvailability = privileges.includes(
    PrivilegeNames.Calendar_ViewEvents_UnitAvailability
  );

  const type = getType(selectedUser, canAccessUnitAvailability);

  const { getQuery, addMutation, deleteMutation, addedField, deletedField, field } =
    queriesMap.get(type);
  const targetUser = type === UserType.USER && selectedUser && { targetUser: selectedUser };

  const weekStartDate = formatDate(getFirstDayOfTheWeek(selectedDate));
  const queryVariables = { ...targetUser };

  const { data, loading, error, backgroundLoading } = useCacheNetworkQuery(getQuery, {
    variables: queryVariables,
    nextFetchPolicy: 'cache-first',
    skip: canAccessAtmList && !selectedUser && !canAccessUnitAvailability
  });

  const unavailablePeriods = data?.[field];
  const { unavailablePeriod, isUserAssignedToEventWithinPeriod } = getSelectedPeriod(
    unavailablePeriods,
    selectedDate
  );

  const [addUnavailablePeriod, { error: addUnavailableError }] = useMutation(addMutation, {
    update: (cache, { data }) => {
      addPeriodToCache(field, cache, data[addedField].UnavailablePeriod);
    }
  });

  const [deleteUnavailablePeriod, { error: deleteUnavailableError }] = useMutation(deleteMutation, {
    update: (cache, { data }) => {
      removePeriodFromCache(field, cache, data[deletedField].DeletedUnavailablePeriod);
    }
  });

  const addNewUnavailablePeriod = async () => {
    await addUnavailablePeriod({
      variables: { period: { Start: weekStartDate }, ...targetUser }
    });
  };

  const removeUnavailablePeriod = async () => {
    if (!unavailablePeriod.Id) return;

    await deleteUnavailablePeriod({
      variables: { id: unavailablePeriod.Id, ...targetUser }
    });
  };

  const handleAvailabilityChange = async checked => {
    checked ? await removeUnavailablePeriod() : await addNewUnavailablePeriod();
  };

  return {
    availability: !unavailablePeriod,
    isUserAssignedToEventWithinPeriod,
    loading,
    error: error || addUnavailableError || deleteUnavailableError,
    backgroundLoading,
    handleAvailabilityChange
  };
};

const UserType = {
  ORG_UNIT: 'ORG_UNIT',
  USER: 'USER'
};

const queriesMap = new Map([
  [
    UserType.ORG_UNIT,
    {
      getQuery: GET_ORG_UNIT_UNAVAILABLE_PERIODS,
      addMutation: ADD_ORG_UNIT_UNAVAILABLE_PERIOD,
      deleteMutation: DELETE_ORG_UNIT_UNAVAILABLE_PERIOD,
      field: 'orgUnitUnavailablePeriods',
      addedField: 'addOrgUnitUnavailablePeriod',
      deletedField: 'deleteOrgUnitUnavailablePeriod'
    }
  ],
  [
    UserType.USER,
    {
      getQuery: GET_UNAVAILABLE_PERIODS,
      addMutation: ADD_UNAVAILABLE_PERIOD,
      deleteMutation: DELETE_UNAVAILABLE_PERIOD,
      field: 'unavailablePeriods',
      addedField: 'addUnavailablePeriod',
      deletedField: 'deleteUnavailablePeriod'
    }
  ]
]);

const getType = (selectedUser, canAccessUnitAvailability) => {
  if (!selectedUser && canAccessUnitAvailability) {
    return UserType.ORG_UNIT;
  }

  return UserType.USER;
};

const getSelectedPeriod = (unavailablePeriods, selectedDate) => {
  if (!unavailablePeriods) {
    return {};
  }

  const weekStart = moment(selectedDate).startOf('week');
  const weekEnd = moment(selectedDate).endOf('week');
  const unavailablePeriod = getUnavailablePeriod(
    weekStart,
    weekEnd,
    unavailablePeriods.filter(period => !period.EventId)
  );
  const isUserAssignedToEventWithinPeriod = isUserAssignedToEvent(
    weekStart,
    weekEnd,
    unavailablePeriods.filter(period => period.EventId)
  );

  return { unavailablePeriod, isUserAssignedToEventWithinPeriod };
};

const getUnavailablePeriod = (weekStart, weekEnd, unavailablePeriods) =>
  unavailablePeriods.find(
    period =>
      moment(period.Start).startOf('week').isSame(weekStart) &&
      moment(period.End).endOf('week').isSame(weekEnd)
  );

const isUserAssignedToEvent = (weekStart, weekEnd, unavailablePeriods) =>
  unavailablePeriods.some(
    period =>
      moment(period.Start).startOf('week').isSameOrBefore(weekEnd) &&
      moment(period.End).startOf('week').isSameOrAfter(weekStart)
  );

export default useAvailability;
